import type { PostProcessorModule } from 'i18next';

export type GlossaryData = {
  [language: string]: {
    [sourcePhrase: string]: string;
  };
};

let combinedPattern: RegExp | null = null;
let replacementMap: Map<string, string> = new Map();

function isAllCaps(text: string): boolean {
  return text === text.toUpperCase() && text !== text.toLowerCase();
}

function isTitleCase(text: string): boolean {
  return text
    .split(/\s+/)
    .every(
      (word) =>
        word.length > 0 &&
        word[0] === word[0].toUpperCase() &&
        (word.length === 1 || word.slice(1) === word.slice(1).toLowerCase()),
    );
}

function isSentenceCase(text: string): boolean {
  const firstChar = text[0];
  return (
    firstChar === firstChar.toUpperCase() &&
    firstChar !== firstChar.toLowerCase() &&
    !isAllCaps(text) &&
    !isTitleCase(text)
  );
}

function toTitleCase(text: string): string {
  return text
    .split(/\s+/)
    .map((word) =>
      word.length > 0
        ? word[0].toUpperCase() + word.slice(1).toLowerCase()
        : word,
    )
    .join(' ');
}

function applyCasing(matched: string, replacement: string): string {
  if (isAllCaps(matched)) {
    return replacement.toUpperCase();
  }
  if (isTitleCase(matched)) {
    return toTitleCase(replacement);
  }
  if (isSentenceCase(matched)) {
    return (
      replacement.charAt(0).toUpperCase() + replacement.slice(1).toLowerCase()
    );
  }
  return replacement.toLowerCase();
}

function escapeRegExp(text: string): string {
  return text.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
}

export function setGlossary(
  glossary: GlossaryData | null | undefined,
  language: string,
): void {
  if (!glossary || !glossary[language]) {
    combinedPattern = null;
    replacementMap = new Map();
    return;
  }

  const languageEntries = glossary[language];
  const sortedPhrases = Object.keys(languageEntries).sort(
    (a, b) => b.length - a.length,
  );

  replacementMap = new Map(
    sortedPhrases.map((phrase) => [
      phrase.toLowerCase(),
      languageEntries[phrase],
    ]),
  );

  const alternatives = sortedPhrases.map(escapeRegExp).join('|');
  combinedPattern = new RegExp(`(?<!\\w)(?:${alternatives})(?!\\w)`, 'gi');
}

export const glossaryPostProcessor: PostProcessorModule = {
  type: 'postProcessor',
  name: 'glossaryReplace',
  process(value: string): string {
    if (!combinedPattern) return value;

    return value.replace(combinedPattern, (matched) => {
      const replacement = replacementMap.get(matched.toLowerCase());
      if (!replacement) return matched;
      return applyCasing(matched, replacement);
    });
  },
};
